import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarHeart, CheckCircle2, Circle, Heart, ListChecks, Loader2, Users, Wallet } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const usd = (n) => `$${Math.round(n || 0).toLocaleString()}`;
const fmt = (d) => (d ? new Date(`${d.slice(0, 10)}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");

function Ring({ pct }) {
  const r = 34, c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 80 80" className="w-24 h-24 -rotate-90" aria-hidden="true">
      <circle cx="40" cy="40" r={r} fill="none" stroke="#EFE8DC" strokeWidth="7" />
      <circle cx="40" cy="40" r={r} fill="none" stroke="#C8A96B" strokeWidth="7" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)} style={{ transition: "stroke-dashoffset 900ms ease" }} />
    </svg>
  );
}

function Card({ title, icon: Icon, to, cta, children, testid }) {
  return (
    <section className="bg-white rounded-3xl border border-[var(--line)] p-5 md:p-6 flex flex-col min-w-0" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4"><Icon className="w-4 h-4 text-[var(--gold)]" /><h2 className="text-[11px] uppercase tracking-[0.22em] text-[#5c544b] flex-1">{title}</h2>
        {to && <Link to={to} className="text-xs font-semibold hover:text-[var(--gold)]" data-testid={`${testid}-link`}>{cta}</Link>}</div>
      {children}
    </section>
  );
}

function Checklist({ c, onToggle }) {
  const pct = c.total ? Math.round((100 * c.done) / c.total) : 0;
  return (
    <Card title="Checklist" icon={ListChecks} to="/dashboard/timeline" cta="Full checklist" testid="hub-checklist">
      <div className="flex items-center gap-4 mb-3"><div className="relative"><Ring pct={pct} /><span className="absolute inset-0 flex items-center justify-center font-serif text-2xl" data-testid="hub-checklist-pct">{pct}%</span></div>
        <div><p className="font-serif text-2xl">{c.done} of {c.total} done</p>{c.overdue > 0 && <p className="text-xs text-red-600" data-testid="hub-overdue">{c.overdue} past due</p>}</div></div>
      <div className="space-y-1">{c.next.map((t) => (
        <button key={t.task_id} onClick={() => onToggle(t)} className="w-full text-left flex items-start gap-2 rounded-xl px-2 py-2 hover:bg-[#f7f3ec] transition-colors" data-testid={`hub-task-${t.task_id}`}>
          <Circle className="w-4 h-4 mt-0.5 text-[var(--gold)] shrink-0" /><span className="flex-1 text-sm">{t.title}</span><span className="text-[11px] text-[#5c544b] shrink-0">{fmt(t.due_date)}</span></button>))}
        {!c.next.length && <p className="text-sm text-[#5c544b] flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" />Everything is done. Enjoy it.</p>}</div>
    </Card>
  );
}

function Budget({ b }) {
  const base = b.target || b.planned;
  const pct = base ? Math.min(100, Math.round((100 * b.paid) / base)) : 0;
  return (
    <Card title="Budget" icon={Wallet} to="/dashboard/budget" cta="Open budget" testid="hub-budget">
      <p className="font-serif text-4xl" data-testid="hub-budget-left">{usd(b.remaining)}</p><p className="text-xs text-[#5c544b]">left to pay of {usd(base)}{b.target ? " budget" : " planned"}</p>
      <div className="h-2 rounded-full bg-[#EFE8DC] mt-4 overflow-hidden"><div className="h-full bg-[var(--gold)] rounded-full transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
      <p className="text-[11px] text-[#5c544b] mt-1">{usd(b.paid)} paid · {b.items} items</p>
      {b.upcoming.length > 0 && <div className="mt-4 space-y-1.5">{b.upcoming.map((i, k) => <p key={k} className="text-sm flex gap-2"><span className="flex-1 truncate">{i.vendor || i.category}</span><span className="text-[#5c544b] text-xs">{usd((i.total_cost || 0) - (i.paid_amount || 0))} by {fmt(i.due_date)}</span></p>)}</div>}
    </Card>
  );
}

function Saved({ s }) {
  return (
    <Card title="Saved vendors" icon={Heart} to="/dashboard/saved-vendors" cta="See shortlist" testid="hub-saved">
      <div className="flex flex-wrap gap-2 mb-4">{Object.entries(s.by_status).filter(([, n]) => n).map(([k, n]) => <span key={k} className="text-[11px] rounded-full bg-[#f7f3ec] px-3 py-1 capitalize">{n} {k}</span>)}</div>
      {s.recent.length ? <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">{s.recent.map((v) => (
        <Link key={v.page_id} to={`/v/${v.page_id}`} className="group rounded-2xl overflow-hidden border border-[var(--line)] min-w-0" data-testid={`hub-saved-${v.page_id}`}>
          <div className="aspect-[4/3] bg-[#EFE8DC] overflow-hidden">{v.cover && <img src={v.cover} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}</div>
          <p className="text-xs font-semibold px-2 pt-1.5 truncate">{v.name}</p><p className="text-[10px] text-[#5c544b] px-2 pb-2 truncate">{v.category}</p></Link>))}</div>
        : <p className="text-sm text-[#5c544b]">Tap the heart on any vendor page to start your shortlist. <Link to="/hub?tab=vendors" className="underline">Browse vendors</Link></p>}
    </Card>
  );
}

export default function PlanningHubPage() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/planning-hub").then((r) => setD(r.data)).catch((e) => setD({ error: formatApiError(e) })), []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (t) => { try { await api.patch(`/timeline/${t.task_id}`, { completed: true }); toast.success("Nice, one less thing"); load(); } catch (e) { toast.error(formatApiError(e)); } };
  if (!d) return <div className="p-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[var(--gold)]" /></div>;
  if (d.error) return <p className="p-10 text-center" data-testid="hub-error">{d.error}</p>;
  return (
    <div className="space-y-6" data-testid="planning-hub-page">
      <header className="flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[240px]"><p className="font-script text-[var(--gold)] text-3xl">Planning Hub</p>
          <h1 className="font-serif font-light text-4xl sm:text-5xl">{d.wedding?.couple_names || "Your wedding"}</h1>
          {d.wedding && <p className="text-sm text-[#5c544b] mt-1">{[d.wedding.venue, d.wedding.city].filter(Boolean).join(" · ")}</p>}</div>
        {d.days_to_go != null && <div className="rounded-3xl bg-[var(--ink)] text-white px-6 py-4 text-center" data-testid="hub-countdown"><p className="font-serif text-4xl">{Math.max(0, d.days_to_go)}</p><p className="text-[10px] uppercase tracking-[0.25em] text-[var(--gold)]">days to go</p></div>}
      </header>
      {!d.wedding && <p className="rounded-2xl bg-amber-50 border border-amber-200 p-4 text-sm" data-testid="hub-no-wedding">Set up your wedding to unlock the checklist and budget. <Link to="/onboarding" className="underline">Start setup</Link></p>}
      <div className="grid lg:grid-cols-3 gap-4 items-stretch">
        {d.checklist && <Checklist c={d.checklist} onToggle={toggle} />}
        {d.budget && <Budget b={d.budget} />}
        {d.guests && <Card title="Guests & RSVPs" icon={Users} to="/dashboard/rsvps" cta="RSVPs" testid="hub-guests">
          <p className="font-serif text-4xl" data-testid="hub-guests-attending">{d.guests.attending}</p><p className="text-xs text-[#5c544b]">attending of {d.guests.total} invited · {d.guests.pending} still to reply</p>
          <Link to="/dashboard/guests" className="btn-outline-gold !py-2 text-xs mt-auto self-start" data-testid="hub-guests-manage"><CalendarHeart className="w-3.5 h-3.5" />Manage guests</Link></Card>}
      </div>
      <Saved s={d.saved} />
    </div>
  );
}
